/* ---------------------------------------------------------------------------
   The field shell's bottom tab bar. Three destinations, and nothing else.

   The desk shell's nav pane offers six screens, the selected structure, and two
   actions on it. This offers three tabs, because an inspector at a structure is
   doing one of three things: finding a bridge, navigating to one, or dealing
   with their own device. Everything the nav pane carries beyond that is desk
   work — see FIELD_SCREENS in src/core/shell.ts for what is absent and why.

   **THE RECORD IS NOT A TAB.** `detail` is pushed from the Bridges list or from
   a map marker, and while it is on screen the Bridges tab reads as the current
   one. A fourth tab for it would be a tab that is meaningless until something
   has been selected, and a tab bar with nothing marked while a record is open
   tells the user they are nowhere.

   **THE MAP TAB REFUSES RATHER THAN DISAPPEARS.** Offline it is disabled and
   says so on the panel it opens. A tab that vanished would move the other two
   under a thumb already reaching for one of them, and reads as a bug rather than
   as a limit.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS, type IconPath } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { t } from '../data/locale.ts';
import type { Screen } from '../data/types.ts';
import { FIELD_TABS } from '../core/shell.ts';

interface Tab {
  screen: Screen;
  label: string;
  iconPath: IconPath;
}

const TABS: Record<string, Tab> = {
  list: { screen: 'list', label: 'Bridges', iconPath: ICONS.list },
  network: { screen: 'network', label: 'Map', iconPath: ICONS.map },
  options: { screen: 'options', label: 'Options', iconPath: ICONS.settings },
};

/**
 * Which tab is lit for a given screen.
 *
 * The record belongs to Bridges. Exported because the check for it should not
 * have to go through the DOM to ask.
 */
export function tabFor(screen: Screen): Screen {
  return screen === 'detail' ? 'list' : screen;
}

/**
 * Why the map cannot be opened, or null when it can.
 *
 * A named function because it is the sentence a user reads, and a rule with its
 * own name is one a check can drive directly — `GR-19`'s habit.
 *
 * `backend` is a proxy: the map needs tile hosts and the register needs `/api`,
 * and in principle a device could reach one and not the other. It is the signal
 * every other screen in the application already reasons about, and a second,
 * separate notion of "offline" would be worse than one imperfect shared one.
 */
export function mapRefusal(s: Readonly<State>): string | null {
  return s.backend === 'unreachable'
    ? 'No connection — the map needs one to fetch tiles. Bridges and their records work offline.'
    : null;
}

export function tabbar(s: Readonly<State>): HTMLElement {
  const current = tabFor(s.screen);

  return h(
    'nav',
    { class: 'tabbar', 'aria-label': 'Main' },
    ...FIELD_TABS.map((name) => {
      const tab = TABS[name];
      if (!tab) return null;
      const on = current === tab.screen;
      const refusal = tab.screen === 'network' ? mapRefusal(s) : null;

      return h(
        'button',
        {
          type: 'button',
          class: ['tabbar__tab', on && 'tabbar__tab--on', refusal && 'tabbar__tab--off'],
          /* `aria-current` rather than `aria-pressed`: these are destinations,
             not toggles, and a screen reader should say "current page". */
          ...(on ? { 'aria-current': 'page' } : {}),
          /* Not `disabled`. The tab still opens — the screen behind it is what
             explains itself, and a control that cannot be activated cannot tell
             you why. Offline it is dimmed and carries the reason as its title. */
          ...(refusal ? { title: refusal } : {}),
          onclick: () => actions.go(tab.screen),
        },
        /* The icon lives inside its own box because MD3 draws the ACTIVE
           INDICATOR as a 64x32 pill behind it — not as a colour change on the
           label, and not as a rule along an edge. A pseudo-element could paint
           it, but then the icon would sit over a shape it is not inside, and
           the two would drift apart the moment either changed size. */
        h('span', { class: 'tabbar__ind' }, icon(tab.iconPath, { size: 24, width: 1.7 })),
        h('span', { class: 'tabbar__label', text: t(tab.label) }),
      );
    }),
  );
}
